import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Close from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { addUnitApi, updateUnitApi } from "../../../services/unit.service";
import UnitSchema from "../../../joi/master/unit.schema";

export default function UnitModal({
  open,
  onClose,
  refetch,
  selectedRow = null,
}) {
  const initialState = {
    active: true,
    unitName: "",
    receivingTransferApproval: "",
    tallyAccountName: "",
    prefixQueueToken: "",
    activeToken: false,
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((preValue) => ({
      ...preValue,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    const { error } = UnitSchema.validate(formData);

    if (error) {
      error.details.forEach((err) => toast.error(err.message));
      return;
    }

    setIsLoading(true);
    if (selectedRow?._id && formData?._id) {
      const { _id, ...payload } = formData;
      response = await updateUnitApi(_id, payload);
    } else {
      response = await addUnitApi(formData);
    }
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(
        response?.message ||
          `Unit ${selectedRow?._id ? "updated" : "added"} successfully`
      );
      setFormData(initialState);
      onClose();
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  // Sync selectedRow data when modal opens (edit) or reset form (add)
  useEffect(() => {
    if (!open) return;
    if (selectedRow?._id) {
      setFormData({
        ...initialState,
        _id: selectedRow._id,
        active: selectedRow?.active ?? true,
        unitName: selectedRow?.unitName || "",
        receivingTransferApproval: selectedRow?.receivingTransferApproval || "",
        tallyAccountName: selectedRow?.tallyAccountName || "",
        prefixQueueToken: selectedRow?.prefixQueueToken || "",
        activeToken: selectedRow?.activeToken ?? false,
      });
    } else {
      setFormData(initialState);
    }
  }, [open, selectedRow]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "20px",
          }}
        >
          <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
            Add Unit
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Unit Name"
                name="unitName"
                required
                fullWidth
                value={formData?.unitName || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={["Auto", "Manual"]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Receiving Transfer Approval"
                    required
                    fullWidth
                  />
                )}
                value={formData?.receivingTransferApproval || ""}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "receivingTransferApproval", value: newValue },
                  })
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Tally Account Name"
                name="tallyAccountName"
                fullWidth
                value={formData?.tallyAccountName || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>

            {/* active */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Profile Queue Token"
                name="prefixQueueToken"
                fullWidth
                value={formData?.prefixQueueToken || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="activeToken"
                    checked={formData?.activeToken}
                    onChange={handleChange}
                  />
                }
                label="Active Token"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="active"
                    checked={formData?.active}
                    onChange={handleChange}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : selectedRow?._id ? (
                "Save Changes"
              ) : (
                `Submit`
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
